import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, LogOut, Package, RefreshCw, Boxes, ClipboardList, TrendingDown, Clock, Download, Ban, Check, X, Home, Images, Palette, Activity, Upload, Trash2, Bell, Search, Pencil, Copy, Undo2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import useInactivityLogout from "@/hooks/useInactivityLogout";
import { FABRICS, COLORS, SWATCHES, slug, unitOf, setFabricUnits, formatDate, formatDateTime, evalFormula, deriveFabricCodes, itemMatchesQuery } from "@/lib/fabricData";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SuppliersTab } from "@/components/SuppliersTab";
import { LowStockTab } from "@/components/LowStockTab";
import { AlteredLabel, AlteredOriginalLine, AlteredCustomerName, DeletedLabel, EditPencil, AlterItemDialog, CustomerPickerDialog } from "@/components/orderAlter";
import { RatesManager } from "@/components/RatesManager";
import { OrderPricing } from "@/components/OrderPricing";
import { FormulaInput } from "@/components/FormulaInput";
import { InstructionsManager } from "@/components/InstructionsManager";
import { CustomerDatabase } from "@/components/CustomerDatabase";
import { CoAdminManager } from "@/components/CoAdminManager";
import { CoAdminActivity } from "@/components/CoAdminActivity";
import { DeliveredCell } from "@/components/DeliveredCell";
import StockPortal from "@/pages/StockPortal";
import CoAdminPortal from "@/pages/CoAdminPortal";
import { ReportsTab } from "@/components/ReportsTab";
import {
  adminLogin,
  getAdminStock,
  getOrders,
  getSummary,
  setStock as apiSetStock,
  refillStock as apiRefill,
  toggleColor as apiToggle,
  bulkToggle as apiBulkToggle,
  setOrderStatus as apiSetStatus,
  cancelOrder as apiCancel,
  setItemDecision as apiItemDecision,
  replyToOrder,
  setStockThreshold,
  getViews,
  updateColorApi,
  getColors,
  getFabrics,
  getAdminUnits,
  addUnit,
  renameUnit,
  deleteUnit,
  addFabric,
  updateFabric,
  deleteFabric,
  reorderFabrics,
  addStockItem,
  deleteStockItem,
  getCatalog,
  uploadCatalog,
  updateCatalog,
  deleteCatalog,
  uploadFabricImage,
  deleteFabricImage,
  uploadStockImage,
  deleteStockImage,
  copyStockImageToAll,
  renameStockColor,
  getAnnouncementAdmin,
  saveAnnouncement,
  auditStock,
  deleteStockById,
  bulkDeleteStock,
  restoreStock,
  getStockHistory,
  adminGetCoadminToken,
  amendDelivered,
  adminAlterItem,
  adminAlterCustomer,
  adminDeleteItem,
  adminRestoreItem,
  adminAcceptAll,
  adminRejectAll,
  getCustomers,
  fileUrl,
  formatApiError,
} from "@/lib/api";

const TOKEN_KEY = "cincstock_admin_token";
// Draggable fabric-type chip (@dnd-kit sortable grid). Grip handle starts the
// drag (mouse + touch); the label selects the fabric. Free 2D drag across a
// wrapping layout — no horizontal scroll.
function FabricReorderItem({ fabric, selected, onSelect }) {
  const id = fabric.id || fabric.name;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative flex items-center ${isDragging ? "bg-[#1a1a1a] scale-[1.04]" : "bg-[#0A0A0A]"}`}
      data-testid={`admin-fabric-item-${slug(fabric.name)}`}
    >
      <span
        {...attributes}
        {...listeners}
        data-testid={`admin-fabric-grip-${slug(fabric.name)}`}
        title="Drag to reorder"
        className="pl-2 pr-1 py-2.5 cursor-grab active:cursor-grabbing text-white/40 hover:text-white touch-none select-none"
      >
        <GripVertical className="h-4 w-4" />
      </span>
      <button
        data-testid={`admin-fabric-tab-${slug(fabric.name)}`}
        onClick={() => onSelect(fabric.name)}
        className={`pr-4 pl-1 py-2.5 text-sm font-medium transition-colors ${selected ? "bg-white text-black" : "text-white hover:bg-white/[0.06]"}`}
      >
        {fabric.name}
        {fabric.code && <span className={`ml-1.5 text-[10px] ${selected ? "text-black/50" : "text-white/35"}`}>{fabric.code}</span>}
      </button>
    </div>
  );
}

// Wrapping, free-drag fabric-type list. No horizontal scroll.
function FabricReorderList({ fabrics, selectedFabric, onSelect, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );
  const ids = fabrics.map((f) => f.id || f.name);
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(fabrics, oldIndex, newIndex));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div
          className="flex flex-wrap gap-px bg-white/10 border border-white/10 mb-4"
          data-testid="admin-fabric-reorder"
        >
          {fabrics.map((f) => (
            <FabricReorderItem
              key={f.id || f.name}
              fabric={f}
              selected={f.name === selectedFabric}
              onSelect={onSelect}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}


const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
const orderUnit = (o) => {
  const u = new Set((o.items || []).map((it) => unitOf(it.fabric)));
  return u.size === 1 ? [...u][0] : "units";
};

function LoginScreen({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem(TOKEN_KEY, res.token);
      onSuccess(res.token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div
        className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: "url(https://images.pexels.com/photos/38362010/pexels-photo-38362010.jpeg)" }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute bottom-0 p-12">
          <span className="font-display font-black text-3xl tracking-tighter text-white">
            CiNCStock<span className="text-white/40">.in</span>
          </span>
          <p className="mt-2 text-white/50 max-w-sm text-sm">Warehouse control room — manage every kg of knitted fabric in real time.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-[#0A0A0A]">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="h-10 w-10 border border-white/20 flex items-center justify-center mb-6">
            <Lock className="h-5 w-5 text-white" />
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter text-white">Admin Access</h1>
          <p className="text-white/40 text-sm mt-2 mb-8">Sign in to manage stock and view bookings.</p>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Email</Label>
              <Input
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@cincstock.in"
                className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-white/50">Password</Label>
              <Input
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-8 w-full rounded-none bg-white text-black hover:bg-white/90 text-sm uppercase tracking-wider font-semibold transition-colors"
          >
            {loading ? "Signing in…" : "Sign In"}
          </Button>
          <Link to="/" data-testid="admin-back-home-link" className="block mt-6 text-center text-xs uppercase tracking-wider text-white/40 hover:text-white transition-colors">
            ← Go back to home page
          </Link>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, unit }) {
  return (
    <div className="bg-[#0A0A0A] p-6">
      <div className="flex items-center gap-2 text-white/40 text-xs uppercase tracking-[0.2em]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-3 font-display font-black text-3xl tracking-tighter text-white tabular">
        {value}
        {unit && <span className="text-white/30 text-lg ml-1">{unit}</span>}
      </p>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [stock, setStockState] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLimit, setOrdersLimit] = useState(20);
  const [summary, setSummary] = useState(null);
  const [selectedFabric, setSelectedFabric] = useState(FABRICS[0].name);
  const [fabrics, setFabrics] = useState(FABRICS);
  const [newColor, setNewColor] = useState({ name: "", hex: "#888888", qty: "0" });
  const [newFabric, setNewFabric] = useState({ name: "", code: "", unit: "kg" });
  const [units, setUnits] = useState([]);
  const [newUnitName, setNewUnitName] = useState("");
  const [alterItemCtx, setAlterItemCtx] = useState(null);
  const [alterCustomerCtx, setAlterCustomerCtx] = useState(null);
  const [thresholds, setThresholds] = useState({});
  const [replies, setReplies] = useState({});
  const [edits, setEdits] = useState({});
  const [filters, setFilters] = useState({ q: "", status: "all", from: "", to: "" });
  const [colors, setColors] = useState([]);
  const [colorEdits, setColorEdits] = useState({});
  const [views, setViews] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [annMsg, setAnnMsg] = useState("");
  const [annEnabled, setAnnEnabled] = useState(false);
  const [annSaving, setAnnSaving] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);

  const loadAnnouncement = async () => {
    try { const a = await getAnnouncementAdmin(token); setAnnMsg(a.message || ""); setAnnEnabled(!!a.enabled); } catch {}
  };
  const handleSaveAnnouncement = async () => {
    setAnnSaving(true);
    try {
      await saveAnnouncement(token, annMsg.slice(0, 499), annEnabled);
      toast.success("Announcement saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAnnSaving(false); }
  };
  const runAudit = async () => {
    setAuditing(true);
    try { setAuditResult(await auditStock(token)); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAuditing(false); }
  };
  const removeFlagged = async (entry) => {
    if (!window.confirm(`Remove ${entry.color} from ${entry.wrong_fabric}? You'll get a few seconds to undo.`)) return;
    try {
      const res = await bulkDeleteStock(token, [entry.id]);
      const removed = res.removed || [];
      await runAudit();
      loadAll(token);
      toast.success(`Removed ${entry.color} from ${entry.wrong_fabric}`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await restoreStock(token, removed);
              toast.success(`Restored ${entry.color}`);
              await runAudit();
              loadAll(token);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          },
        },
      });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [auditSelected, setAuditSelected] = useState({});
  const auditSelectedIds = Object.keys(auditSelected).filter((k) => auditSelected[k]);
  const toggleAuditSelect = (id) => setAuditSelected((p) => ({ ...p, [id]: !p[id] }));
  const auditAllSelected = (rows) => rows.length > 0 && rows.every((r) => auditSelected[r.id]);
  const selectAllAudit = (rows, on) =>
    setAuditSelected((p) => { const n = { ...p }; rows.forEach((r) => (n[r.id] = on)); return n; });
  const deleteAuditSelected = async () => {
    if (auditSelectedIds.length === 0) return;
    if (!window.confirm(`Permanently delete ${auditSelectedIds.length} selected flagged entr(ies)? You'll get a few seconds to undo.`)) return;
    try {
      const res = await bulkDeleteStock(token, auditSelectedIds);
      const removed = res.removed || [];
      setAuditSelected({});
      await runAudit();
      loadAll(token);
      toast.success(`Deleted ${res.deleted} entr(ies)`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const r = await restoreStock(token, removed);
              toast.success(`Restored ${r.restored} entr(ies)`);
              await runAudit();
              loadAll(token);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          },
        },
      });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [historyFilters, setHistoryFilters] = useState({ fabric: "all", color: "", date_from: "", date_to: "" });
  const bulkColorInput = useRef(null);
  const [catalog, setCatalog] = useState([]);
  const [catalogFabric, setCatalogFabric] = useState("");
  const [catalogEdits, setCatalogEdits] = useState({});
  const [newOrders, setNewOrders] = useState(0);
  const prevOrderCount = useRef(null);
  const filtersRef = useRef(filters);
  const ordersLimitRef = useRef(20);
  const [selected, setSelected] = useState({});
  const [stockQuery, setStockQuery] = useState("");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  // Admin sessions never auto-logout (per owner request). Co-Admin & Customer
  // keep their 5-minute inactivity logout in their own components.

  const loadOrders = async (tk) => {
    const f = filtersRef.current;
    try {
      const res = await getOrders(tk, { q: f.q, status: f.status, from: f.from, to: f.to, skip: 0, limit: ordersLimitRef.current });
      setOrders(res.items);
      setOrdersTotal(res.total);
      // New-order detection only makes sense against the unfiltered total, so
      // skip it while any filter is active (otherwise switching filters would
      // look like orders appeared/disappeared).
      const isDefault = !f.q && f.status === "all" && !f.from && !f.to;
      if (isDefault) {
        if (prevOrderCount.current !== null && res.total > prevOrderCount.current) {
          const diff = res.total - prevOrderCount.current;
          setNewOrders((n) => n + diff);
          toast.success(`${diff} new order${diff > 1 ? "s" : ""} received!`, { description: "Check the Orders tab." });
        }
        prevOrderCount.current = res.total;
      }
    } catch (e) {
      if (e.response?.status === 401) { toast.error("Session expired, please sign in again"); logout(); }
    }
  };

  const loadAll = async (tk) => {
    try {
      const [s, sum, cs, vs, cat] = await Promise.all([
        getAdminStock(tk), getSummary(tk), getColors(), getViews(tk), getCatalog(),
      ]);
      setStockState(s);
      setSummary(sum);
      setColors(cs);
      setViews(vs);
      setCatalog(cat);
      const fs = await getFabrics();
      if (fs && fs.length) {
        setFabrics(fs);
        setFabricUnits(fs);
        setSelectedFabric((cur) => (fs.some((f) => f.name === cur) ? cur : fs[0].name));
      }
      const us = await getAdminUnits(tk);
      if (us) {
        setUnits(us);
        setNewFabric((p) => (us.some((u) => u.name === p.unit) ? p : { ...p, unit: us[0]?.name || "kg" }));
      }
      await loadOrders(tk);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired, please sign in again");
        logout();
      }
    }
  };

  useEffect(() => {
    if (!token) return;
    loadAll(token);
    const t = setInterval(() => loadAll(token), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Keep refs in sync and refetch orders when filters or the loaded batch size
  // change (debounced so typing in the search box doesn't spam the backend).
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { ordersLimitRef.current = ordersLimit; }, [ordersLimit]);
  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => loadOrders(token), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, ordersLimit, token]);

  const fabricRows = useMemo(() => stock.filter((i) => i.fabric === selectedFabric), [stock, selectedFabric]);
  const codeMap = useMemo(() => deriveFabricCodes(stock, fabrics), [stock, fabrics]);
  // Role switching (Admin can preview Customer / Co-Admin views without logout)
  const [activeView, setActiveView] = useState("admin");
  const [coadminToken, setCoadminToken] = useState("");
  const [switching, setSwitching] = useState(false);
  // Client-side "Load More" caps for list panels (20 at a time)
  const [activityLimit, setActivityLimit] = useState(20);
  const [historyLimit, setHistoryLimit] = useState(20);
  useEffect(() => { setHistoryLimit(20); }, [historyFilters]);
  // Badge count only — the Low Stock panel itself (LowStockTab) manages its own threshold/search.
  const lowStockRows = useMemo(
    () => stock.filter((i) => i.active !== false && (i.available_kg ?? 0) <= lowStockThreshold),
    [stock, lowStockThreshold]
  );
  const selectedFabricImage = useMemo(() => fabrics.find((f) => f.name === selectedFabric)?.image_path || null, [fabrics, selectedFabric]);
  const isStockSearching = stockQuery.trim().length > 0;
  const displayRows = useMemo(() => {
    if (!stockQuery.trim()) return fabricRows;
    return stock.filter((i) => itemMatchesQuery(i, stockQuery, codeMap));
  }, [stock, fabricRows, stockQuery, codeMap]);

  // Orders are filtered, sorted (Pending first, newest) and paginated by the
  // backend. `orders` already holds the current page batch.
  const filteredOrders = orders;

  const clearFilters = () => { setFilters({ q: "", status: "all", from: "", to: "" }); setOrdersLimit(20); };

  const handleSet = async (item) => {
    const val = evalFormula(edits[`${item.fabric}-${item.color}`]);
    if (val === null || val < 0) return toast.error("Enter a valid quantity (numbers or a formula like 100+50)");
    try {
      await apiSetStock(token, item.fabric, item.color, val);
      toast.success(`${item.fabric} · ${item.color} set to ${fmt(val)} kg`);
      setEdits((p) => ({ ...p, [`${item.fabric}-${item.color}`]: "" }));
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleRefill = async (item, amount) => {
    try {
      await apiRefill(token, item.fabric, item.color, amount);
      toast.success(`Added ${fmt(amount)} kg to ${item.fabric} · ${item.color}`);
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleToggle = async (item, active) => {
    try {
      await apiToggle(token, item.fabric, item.color, active);
      toast.success(`${item.fabric} · ${item.color} ${active ? "shown to customers" : "hidden from customers"}`);
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const selKey = (i) => `${i.fabric}|${i.color}`;
  const selectedList = Object.keys(selected).filter((k) => selected[k]);
  const toggleSelect = (i) => setSelected((p) => ({ ...p, [selKey(i)]: !p[selKey(i)] }));
  const allSelectedInFabric = (rows) => rows.length > 0 && rows.every((i) => selected[selKey(i)]);
  const selectAllFabric = (rows, on) =>
    setSelected((p) => { const n = { ...p }; rows.forEach((i) => (n[selKey(i)] = on)); return n; });

  const runBulk = async (active) => {
    const targets = selectedList.map((k) => { const [fabric, color] = k.split("|"); return { fabric, color }; });
    if (targets.length === 0) return;
    try {
      await apiBulkToggle(token, targets, active);
      toast.success(`${targets.length} colour(s) ${active ? "shown" : "hidden"}`);
      setSelected({});
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const runBulkDelete = async () => {
    const rows = stock.filter((i) => selected[selKey(i)]);
    const ids = rows.map((i) => i.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected item(s)? You'll get a few seconds to undo.`)) return;
    try {
      const res = await bulkDeleteStock(token, ids);
      const removed = res.removed || rows;
      setSelected({});
      loadAll(token);
      toast.success(`Deleted ${res.deleted} item(s)`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const r = await restoreStock(token, removed);
              toast.success(`Restored ${r.restored} item(s)`);
              loadAll(token);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          },
        },
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const globalFabricToggle = async (rows, active) => {
    const targets = rows.map((i) => ({ fabric: i.fabric, color: i.color }));
    try {
      await apiBulkToggle(token, targets, active);
      toast.success(`All ${targets.length} colours of ${selectedFabric} ${active ? "shown" : "hidden"}`);
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleStatus = async (order, status) => {
    if (status === "Approved") {
      const short = (order.items || [])
        .filter((it) => it.status !== "Rejected")
        .map((it) => {
          const st = stock.find((s) => s.fabric === it.fabric && s.color === it.color);
          const avail = st ? st.available_kg : 0;
          return avail < it.quantity_kg ? `${it.fabric} · ${it.color} (need ${it.quantity_kg}, have ${avail})` : null;
        })
        .filter(Boolean);
      if (short.length > 0 && !window.confirm(`Warning: approving will push these below zero:\n\n${short.join("\n")}\n\nApprove anyway?`))
        return;
    }
    try {
      await apiSetStatus(token, order.id, status);
      toast.success(`Order marked ${status}`);
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleAddColor = async () => {
    if (!newColor.name.trim()) return toast.error("Enter a colour name");
    try {
      await addStockItem(token, selectedFabric, newColor.name.trim(), newColor.hex, evalFormula(newColor.qty) ?? 0);
      toast.success(`Added ${newColor.name} to ${selectedFabric}`);
      setNewColor({ name: "", hex: "#888888", qty: "0" });
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDeleteColorRow = async (item) => {
    if (!window.confirm(`Delete ${item.color} from ${item.fabric}? You'll get a few seconds to undo.`)) return;
    try {
      const res = await bulkDeleteStock(token, [item.id]);
      const removed = res.removed || [item];
      loadAll(token);
      toast.success(`Deleted ${item.color} from ${item.fabric}`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await restoreStock(token, removed);
              toast.success(`Restored ${item.color}`);
              loadAll(token);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          },
        },
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAddFabric = async () => {
    if (!newFabric.name.trim()) return toast.error("Enter an item/fabric name");
    try {
      await addFabric(token, newFabric.name.trim(), newFabric.code.trim(), newFabric.unit || "kg");
      toast.success(`Added ${newFabric.name}`);
      setNewFabric({ name: "", code: "", unit: "kg" });
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleRenameFabric = async () => {
    const f = fabrics.find((x) => x.name === selectedFabric);
    if (!f) return;
    const name = window.prompt("Item/fabric name:", f.name);
    if (name === null) return;
    const code = window.prompt("Short code (optional):", f.code || "");
    try {
      // Rename only touches name/code — the unit is deliberately NOT sent so a
      // rename can never overwrite this (or any other) item's unit.
      await updateFabric(token, f.id, name.trim(), (code || "").trim());
      toast.success("Item updated");
      setSelectedFabric(name.trim());
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleChangeUnit = async (unit) => {
    const f = fabrics.find((x) => x.name === selectedFabric);
    if (!f) return;
    try {
      await updateFabric(token, f.id, f.name, f.code || "", unit);
      toast.success(`Unit set to ${unit}`);
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fabricsRef = useRef(fabrics);
  useEffect(() => { fabricsRef.current = fabrics; }, [fabrics]);
  const persistFabricOrder = async (list) => {
    const arr = list || fabricsRef.current;
    const ids = arr.map((f) => f.id).filter(Boolean);
    if (!ids.length) return;
    try { await reorderFabrics(token, ids); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save new order"); }
  };

  const handleAddUnit = async () => {
    const name = newUnitName.trim();
    if (!name) return toast.error("Enter a unit name");
    try {
      await addUnit(token, name);
      toast.success(`Added unit "${name}"`);
      setNewUnitName("");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleRenameUnit = async (u) => {
    const name = window.prompt("Rename unit:", u.name);
    if (name === null) return;
    if (!name.trim()) return toast.error("Unit name cannot be empty");
    try {
      await renameUnit(token, u.id, name.trim());
      toast.success("Unit renamed — updated everywhere it was used");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDeleteUnit = async (u) => {
    if (!window.confirm(`Delete unit "${u.name}"?\n\nItems already using it keep their value — it just won't appear in the dropdown for future assignments.`)) return;
    try {
      await deleteUnit(token, u.id);
      toast.success(`Deleted unit "${u.name}"`);
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveThreshold = async (item) => {
    const key = `${item.fabric}-${item.color}`;
    const raw = thresholds[key];
    if (raw === undefined) return;
    const val = raw === "" ? 0 : evalFormula(raw);
    if (val === null || val < 0) return toast.error("Enter a valid alert quantity (numbers or a formula)");
    if ((item.min_kg ?? 0) === val) return;
    try {
      await setStockThreshold(token, item.id, val);
      toast.success(`Alert threshold set for ${item.color}`);
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveReply = async (order) => {
    const reply = (replies[order.id] ?? order.admin_reply ?? "").trim();
    try {
      await replyToOrder(token, order.id, reply);
      toast.success("Reply sent to customer");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDeleteFabric = async () => {
    const f = fabrics.find((x) => x.name === selectedFabric);
    if (!f) return;
    if (!window.confirm(`Delete fabric "${f.name}" and all its colours?`)) return;
    try {
      await deleteFabric(token, f.id);
      toast.success(`Deleted fabric ${f.name}`);
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fabricImgInput = useRef(null);
  const [uploadingFabricImg, setUploadingFabricImg] = useState(false);
  const handleFabricImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const f = fabrics.find((x) => x.name === selectedFabric);
    if (!f) return;
    setUploadingFabricImg(true);
    try {
      await uploadFabricImage(token, f.id, file);
      toast.success(`Image uploaded for ${selectedFabric}`);
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingFabricImg(false);
    }
  };
  const handleDeleteFabricImage = async () => {
    const f = fabrics.find((x) => x.name === selectedFabric);
    if (!f) return;
    try {
      await deleteFabricImage(token, f.id);
      toast.success(`Image removed from ${selectedFabric}`);
      loadAll(token);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // --- Per-colour image + inline rename ---
  const colorImgInput = useRef(null);
  const colorUploadTarget = useRef(null);
  const [renameColorId, setRenameColorId] = useState(null);
  const [renameColorVal, setRenameColorVal] = useState("");
  const [editSwatchId, setEditSwatchId] = useState(null);
  const [swatchVal, setSwatchVal] = useState("#888888");
  const colorByName = useMemo(() => Object.fromEntries(colors.map((c) => [c.name, c])), [colors]);

  const triggerColorImage = (stockItem) => {
    if (!stockItem) return;
    colorUploadTarget.current = { fabric: stockItem.fabric, color: stockItem.color };
    colorImgInput.current?.click();
  };
  const handleColorImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const t = colorUploadTarget.current;
    if (!file || !t) return;
    try {
      await uploadStockImage(token, t.fabric, t.color, file);
      toast.success(`Image saved for ${t.color} · ${t.fabric}`);
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const handleCopyImageToAll = async (item) => {
    try {
      const res = await copyStockImageToAll(token, item.fabric, item.color);
      if (res.updated > 0) toast.success(`Copied ${item.color} image to ${res.updated} fabric(s)`);
      else toast.info(`Every fabric with ${item.color} already has its own image`);
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const startRenameColor = (stockItem) => {
    setRenameColorId(stockItem.id);
    setRenameColorVal(stockItem.color);
  };
  const saveRenameColor = async (stockItem) => {
    const name = renameColorVal.trim();
    if (!name || name === stockItem.color) { setRenameColorId(null); return; }
    try {
      await renameStockColor(token, stockItem.id, name);
      toast.success(`Renamed to ${name} · ${stockItem.fabric}`);
      setRenameColorId(null);
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startEditSwatch = (colorDoc) => {
    setEditSwatchId(colorDoc.id);
    setSwatchVal(colorDoc.hex || "#888888");
  };
  const saveSwatch = async (colorDoc) => {
    let hex = swatchVal.trim();
    if (!hex.startsWith("#")) hex = `#${hex}`;
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return toast.error("Enter a valid hex colour, e.g. #3B82F6");
    if (hex.toLowerCase() === (colorDoc.hex || "").toLowerCase()) { setEditSwatchId(null); return; }
    try {
      await updateColorApi(token, colorDoc.id, colorDoc.name, hex);
      toast.success(`Swatch updated for ${colorDoc.name}`);
      setEditSwatchId(null);
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkFabric, setBulkFabric] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const colorsForFabric = (fab) => {
    const uniq = [...new Set(stock.filter((s) => s.fabric === fab).map((s) => s.color))];
    return uniq.map((n) => colorByName[n]).filter(Boolean);
  };

  const matchColorName = (filename, fabricName, colorNames) => {
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    let target = norm(filename.replace(/\.[^.]+$/, ""));
    const fabNorm = norm(fabricName);
    const fabCode = norm(fabrics.find((f) => f.name === fabricName)?.code || "");
    if (fabNorm && target.startsWith(fabNorm)) target = target.slice(fabNorm.length);
    else if (fabCode && target.startsWith(fabCode)) target = target.slice(fabCode.length);
    if (!target) return "";
    let found = colorNames.find((c) => norm(c) === target);
    if (found) return found;
    const cands = colorNames.filter((c) => norm(c) === target || target.includes(norm(c)) || norm(c).includes(target));
    cands.sort((a, b) => norm(b).length - norm(a).length);
    return cands[0] || "";
  };

  const buildBulkPreview = (files, fab) => {
    const names = colorsForFabric(fab).map((c) => c.name);
    const rows = files.map((file, idx) => ({
      key: `${idx}-${file.name}`,
      filename: file.name,
      file,
      preview: URL.createObjectURL(file),
      colorName: matchColorName(file.name, fab, names),
    }));
    setBulkFabric(fab);
    setBulkRows(rows);
    setBulkOpen(true);
  };

  const handleBulkColorImages = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    buildBulkPreview(files, selectedFabric);
  };

  // Drag & drop (supports dropping a whole folder of images)
  const [dragOver, setDragOver] = useState(false);
  const filesFromDataTransfer = async (dt) => {
    const items = dt.items ? Array.from(dt.items) : [];
    const canEntries = items.length && items[0].webkitGetAsEntry;
    if (!canEntries) return Array.from(dt.files || []).filter((f) => f.type.startsWith("image/"));
    const entries = items.map((it) => it.webkitGetAsEntry && it.webkitGetAsEntry()).filter(Boolean);
    const out = [];
    const walk = (entry) => new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((f) => { out.push(f); resolve(); }, () => resolve());
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const readBatch = () => reader.readEntries(async (batch) => {
          if (!batch.length) return resolve();
          for (const en of batch) await walk(en);
          readBatch();
        }, () => resolve());
        readBatch();
      } else resolve();
    });
    for (const en of entries) await walk(en);
    return out.filter((f) => f.type && f.type.startsWith("image/"));
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = await filesFromDataTransfer(e.dataTransfer);
    if (!files.length) return toast.error("No image files found in the drop");
    buildBulkPreview(files, selectedFabric);
  };

  const setBulkRowColor = (key, colorName) =>
    setBulkRows((prev) => prev.map((r) => (r.key === key ? { ...r, colorName } : r)));

  const confirmBulkUpload = async () => {
    const cols = colorsForFabric(bulkFabric);
    const validNames = new Set(cols.map((c) => c.name));
    const toUpload = bulkRows.filter((r) => r.colorName && validNames.has(r.colorName));
    if (!toUpload.length) return toast.error("Assign at least one image to a colour first");
    setBulkSaving(true);
    let ok = 0, fail = 0;
    for (const r of toUpload) {
      try { await uploadStockImage(token, bulkFabric, r.colorName, r.file); ok++; }
      catch { fail++; }
    }
    setBulkSaving(false);
    bulkRows.forEach((r) => r.preview && URL.revokeObjectURL(r.preview));
    setBulkRows([]);
    setBulkOpen(false);
    toast.success(`Saved ${ok} image(s) to ${bulkFabric}${fail ? `, ${fail} failed` : ""}`);
    loadAll(token);
  };

  const loadStockHistory = async (f = historyFilters) => {
    try {
      const params = {
        fabric: f.fabric && f.fabric !== "all" ? f.fabric : "",
        color: f.color || "",
        date_from: f.date_from || "",
        date_to: f.date_to || "",
      };
      const data = await getStockHistory(token, params);
      setStockHistory(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportStockHistory = () => {
    if (!stockHistory.length) return toast.error("No stock history to export");
    const headers = ["Fabric", "Colour", "Previous Qty", "New Qty", "Change Type", "Order", "Date & Time"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = stockHistory.map((h) => [
      h.fabric, h.color, h.prev_qty, h.new_qty, h.change_type,
      h.order_id ? `${h.order_ref || ""}${h.order_company ? " / " + h.order_company : ""} #${String(h.order_id).slice(-6)}` : "",
      h.created_at ? formatDateTime(h.created_at) : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-history-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${stockHistory.length} row(s)`);
  };

  const handleCancel = async (order) => {
    try {
      await apiCancel(token, order.id);
      toast.success("Order cancelled — stock returned");
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleItemDecision = async (order, index, decision) => {
    const msg = decision === "Accepted" ? "Item accepted" : decision === "Rejected" ? "Item rejected — stock returned" : "Item reset to pending";
    try {
      await apiItemDecision(token, order.id, index, decision);
      toast.success(msg);
      loadAll(token);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelivered = async (order, index, delivered_kg) => {
    await amendDelivered(token, order.id, index, delivered_kg);
    loadAll(token);
  };

  const handleAlterItem = async (fabric, color) => {
    if (!alterItemCtx) return;
    await adminAlterItem(token, alterItemCtx.order.id, alterItemCtx.index, fabric, color);
    toast.success("Item altered");
    loadAll(token);
  };
  const handleAlterCustomer = async (cust) => {
    if (!alterCustomerCtx) return;
    try {
      await adminAlterCustomer(token, alterCustomerCtx.order.id, cust.id);
      toast.success("Customer changed on this order");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleDeleteItem = async (order, index) => {
    if (!window.confirm("Delete this item? Its quantity is returned to stock; the row stays visible struck-through as 'admin del'.")) return;
    try {
      await adminDeleteItem(token, order.id, index);
      toast.success("Item deleted — qty returned to stock");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const handleAcceptAll = async (order) => {
    try {
      await adminAcceptAll(token, order.id);
      toast.success("All pending items accepted");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const handleRejectAll = async (order) => {
    if (!window.confirm("Reject all pending items on this order?")) return;
    try {
      await adminRejectAll(token, order.id);
      toast.success("All pending items rejected");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const handleRestoreItem = async (order, index) => {
    try {
      await adminRestoreItem(token, order.id, index);
      toast.success("Item restored — qty re-deducted from stock");
      loadAll(token);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCSV = async () => {
    let all = [];
    try {
      const res = await getOrders(token, { q: filters.q, status: filters.status, from: filters.from, to: filters.to, skip: 0, limit: 0 });
      all = res.items || [];
    } catch (e) { return toast.error(formatApiError(e.response?.data?.detail)); }
    const header = ["Order ID", "Date", "Order Status", "Client", "Company", "Phone", "Fabric", "Colour", "Quantity", "Unit", "Delivered", "Delivered By", "Item Status", "Order Total"];
    const rows = [header];
    all.forEach((o) => {
      (o.items || []).forEach((it) => {
        rows.push([
          o.id,
          o.created_at ? formatDateTime(o.created_at) : "",
          o.status || "Pending",
          o.client_name,
          o.company_name,
          o.phone,
          it.fabric,
          it.color,
          it.quantity_kg,
          unitOf(it.fabric),
          it.delivered_kg === null || it.delivered_kg === undefined ? "" : it.delivered_kg,
          it.delivered_by === "coadmin" ? "Co-Admin" : it.delivered_by === "admin" ? "Admin" : "",
          o.status === "Cancelled" ? "Cancelled" : (it.status || "Pending"),
          o.total_kg,
        ]);
      });
    });
    if (rows.length === 1) return toast.error("No orders match the current filter");
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cincstock-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${all.length} order(s)`);
  };

  const handleColorSave = async (c) => {
    const e = colorEdits[c.id] || {};
    const name = e.name !== undefined ? e.name : c.name;
    const hex = e.hex !== undefined ? e.hex : c.hex;
    try {
      await updateColorApi(token, c.id, name, hex);
      toast.success(`Colour updated: ${name}`);
      setColorEdits((p) => ({ ...p, [c.id]: undefined }));
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCatalogUpload = async (file) => {
    if (!file) return;
    try {
      await uploadCatalog(token, file, file.name, catalogFabric);
      toast.success("Image uploaded to catalog");
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
  };

  const handleCatalogSave = async (item) => {
    const e = catalogEdits[item.id] || {};
    const title = e.title !== undefined ? e.title : (item.title || "");
    const fabric = e.fabric !== undefined ? e.fabric : (item.fabric || "");
    try {
      await updateCatalog(token, item.id, { title, fabric });
      toast.success("Catalog image updated");
      setCatalogEdits((p) => { const n = { ...p }; delete n[item.id]; return n; });
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleCatalogDelete = async (id) => {
    try {
      await deleteCatalog(token, id);
      toast.success("Removed from catalog");
      loadAll(token);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const STATUS_STYLES = {
    Pending: "border-yellow-500/40 text-yellow-400",
    Approved: "border-emerald-500/40 text-emerald-400",
    Rejected: "border-red-500/40 text-red-400",
    Hold: "border-orange-500/40 text-orange-400",
    Cancelled: "border-red-500/40 text-red-400",
  };

  const ITEM_STYLES = {
    Pending: "text-white/40",
    Accepted: "text-emerald-400",
    Rejected: "text-red-400 line-through",
  };

  if (!token) return <LoginScreen onSuccess={setToken} />;

  const switchToView = async (v) => {
    if (v === "coadmin") {
      setSwitching(true);
      try {
        const d = await adminGetCoadminToken(token);
        setCoadminToken(d.token);
        setActiveView("coadmin");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setSwitching(false); }
    } else {
      setActiveView(v);
    }
  };

  const BackToAdminBar = (
    <button
      onClick={() => setActiveView("admin")}
      data-testid="back-to-admin-button"
      className="fixed bottom-5 right-5 z-[999] flex items-center gap-2 rounded-none bg-white text-black px-5 h-11 shadow-2xl shadow-black/50 text-xs uppercase tracking-wider font-bold hover:bg-white/90 transition-colors border-2 border-black/20"
    >
      <Lock className="h-4 w-4" /> Back to Admin
    </button>
  );

  if (activeView === "customer") {
    return (
      <div className="relative" data-testid="admin-as-customer-view">
        <StockPortal embeddedExit={() => setActiveView("admin")} />
        {BackToAdminBar}
      </div>
    );
  }
  if (activeView === "coadmin" && coadminToken) {
    return (
      <div className="relative" data-testid="admin-as-coadmin-view">
        <CoAdminPortal injectedToken={coadminToken} onExitEmbed={() => setActiveView("admin")} />
        {BackToAdminBar}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" data-testid="admin-home-logo-link">
              <div className="h-8 w-8 border border-white/20 flex items-center justify-center">
                <Package className="h-4 w-4 text-white" />
              </div>
              <span className="font-display font-black text-xl tracking-tighter text-white">
                CiNCStock<span className="text-white/40">.in</span>
              </span>
            </Link>
            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-black bg-white px-2 py-0.5">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => switchToView("customer")}
              data-testid="switch-to-customer-button"
              variant="outline"
              className="rounded-none border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500 hover:text-black text-xs uppercase tracking-wider transition-colors"
            >
              <Home className="h-3.5 w-3.5 sm:mr-2" /><span className="hidden sm:inline">Customer View</span>
            </Button>
            <Button
              onClick={() => switchToView("coadmin")}
              disabled={switching}
              data-testid="switch-to-coadmin-button"
              variant="outline"
              className="rounded-none border-sky-500/40 bg-transparent text-sky-300 hover:bg-sky-500 hover:text-black text-xs uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              <Boxes className="h-3.5 w-3.5 sm:mr-2" /><span className="hidden sm:inline">{switching ? "Opening…" : "Co-Admin View"}</span>
            </Button>
            <Link to="/catalogs">
              <Button variant="outline" data-testid="admin-catalogs-link" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                <Images className="h-3.5 w-3.5 sm:mr-2" /><span className="hidden sm:inline">Catalogs</span>
              </Button>
            </Link>
            <Button
              onClick={logout}
              data-testid="admin-logout-button"
              variant="outline"
              className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors"
            >
              <LogOut className="h-3.5 w-3.5 sm:mr-2" /><span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8">
          <StatCard icon={Boxes} label="Available" value={summary ? fmt(summary.total_available_kg) : "—"} unit="kg" />
          <StatCard icon={TrendingDown} label="Ordered" value={summary ? fmt(summary.total_booked_kg) : "—"} unit="kg" />
          <StatCard icon={ClipboardList} label="Orders" value={summary ? summary.order_count : "—"} />
          <StatCard icon={Clock} label="Pending" value={summary ? summary.pending_count : "—"} />
        </div>

        <Tabs defaultValue="stock" className="w-full">
          <TabsList className="rounded-none bg-[#141414] border border-white/10 p-1 h-auto flex-wrap">
            <TabsTrigger value="stock" data-testid="tab-stock" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Manage Stock
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" onClick={() => setNewOrders(0)} className="relative rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Orders
              {newOrders > 0 && (
                <span data-testid="new-order-badge" className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 bg-red-500 text-white text-[10px] tabular animate-pulse">
                  {newOrders}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="colors" data-testid="tab-colors" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Colours
            </TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-rates" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Rates
            </TabsTrigger>
            <TabsTrigger value="catalog" data-testid="tab-catalog" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Catalogs
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Activity
            </TabsTrigger>
            <TabsTrigger value="low-stock" data-testid="tab-low-stock" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Low Stock{lowStockRows.length > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-4 h-4 px-1 bg-red-500 text-white text-[10px] tabular" data-testid="low-stock-count">{lowStockRows.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="stock-history" data-testid="tab-stock-history" onClick={() => loadStockHistory()} className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Stock History
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Reports
            </TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="tab-suppliers" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Suppliers
            </TabsTrigger>
            <TabsTrigger value="instructions" data-testid="tab-instructions-admin" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Instructions
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Customers
            </TabsTrigger>
            <TabsTrigger value="coadmin" data-testid="tab-coadmin" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Co-Admin
            </TabsTrigger>
            <TabsTrigger value="announcement" data-testid="tab-announcement" onClick={() => loadAnnouncement()} className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black text-white/60 text-xs uppercase tracking-wider px-4 py-2">
              Announcement
            </TabsTrigger>
          </TabsList>

          {/* Stock management */}
          <TabsContent value="stock" className="mt-6">
            <input ref={colorImgInput} type="file" accept="image/*" onChange={handleColorImageFile} className="hidden" data-testid="color-image-input" />
            <input ref={bulkColorInput} type="file" accept="image/*" multiple onChange={handleBulkColorImages} className="hidden" data-testid="bulk-color-input" />
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
              onDrop={handleDrop}
              data-testid="bulk-dropzone"
              className={`flex flex-wrap items-center justify-between gap-3 mb-4 p-4 border border-dashed transition-colors ${dragOver ? "border-white/60 bg-white/[0.06]" : "border-white/15 bg-[#0A0A0A]"}`}
            >
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">Bulk colour images · {selectedFabric}</p>
                <p className="text-xs text-white/50 mt-0.5">Drag &amp; drop a folder or images here, or click the button. Files auto-match to colours by name.</p>
              </div>
              <Button
                onClick={() => bulkColorInput.current?.click()}
                data-testid="bulk-color-upload-btn"
                className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider transition-colors"
              >
                <Upload className="h-3.5 w-3.5 mr-2" />Bulk Images · {selectedFabric}
              </Button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                data-testid="admin-stock-search"
                value={stockQuery}
                onChange={(e) => setStockQuery(e.target.value)}
                placeholder="Search any fabric type or colour…"
                className="rounded-none h-10 bg-[#141414] border-white/10 pl-9 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30"
              />
            </div>
            <FabricReorderList
              fabrics={fabrics}
              selectedFabric={selectedFabric}
              onSelect={setSelectedFabric}
              onReorder={(next) => { setFabrics(next); persistFabricOrder(next); }}
            />

            {/* Fabric image + type management */}
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 border border-white/10 bg-[#0A0A0A]">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Fabric image</span>
              <div className="h-14 w-14 border border-white/15 bg-[#141414] flex items-center justify-center overflow-hidden shrink-0" data-testid="fabric-image-preview">
                {selectedFabricImage ? (
                  <img src={fileUrl(selectedFabricImage)} alt={selectedFabric} className="h-full w-full object-cover" />
                ) : (
                  <Images className="h-5 w-5 text-white/25" />
                )}
              </div>
              <input ref={fabricImgInput} type="file" accept="image/*" onChange={handleFabricImage} className="hidden" data-testid="fabric-image-input" />
              <Button onClick={() => fabricImgInput.current?.click()} disabled={uploadingFabricImg} data-testid="fabric-image-upload" className="rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors">
                <Upload className="h-3.5 w-3.5 mr-1.5" />{uploadingFabricImg ? "Uploading…" : "Upload Image"}
              </Button>
              {selectedFabricImage && (
                <Button onClick={handleDeleteFabricImage} data-testid="fabric-image-remove" variant="outline" className="rounded-none h-8 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase transition-colors">Remove</Button>
              )}
            </div>

            {/* Fabric type management */}
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 border border-white/10 bg-[#0A0A0A]">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Item / fabric</span>
              <Button onClick={handleRenameFabric} data-testid="fabric-rename" variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase transition-colors">Rename</Button>
              <Button onClick={handleDeleteFabric} data-testid="fabric-delete" variant="outline" className="rounded-none h-8 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase transition-colors">Delete</Button>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Unit</span>
                <Select value={fabrics.find((f) => f.name === selectedFabric)?.unit || "kg"} onValueChange={handleChangeUnit}>
                  <SelectTrigger data-testid="fabric-unit-select" className="h-8 w-28 rounded-none bg-[#141414] border-white/10 text-white text-xs focus:ring-2 focus:ring-white/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                    {units.map((u) => (<SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="h-5 w-px bg-white/15 mx-1" />
              <Input value={newFabric.name} onChange={(e) => setNewFabric((p) => ({ ...p, name: e.target.value }))} placeholder="New item / fabric name" data-testid="new-fabric-name" className="rounded-none h-8 w-40 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
              <Input value={newFabric.code} onChange={(e) => setNewFabric((p) => ({ ...p, code: e.target.value }))} placeholder="Code" data-testid="new-fabric-code" className="rounded-none h-8 w-24 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
              <Select value={newFabric.unit} onValueChange={(v) => setNewFabric((p) => ({ ...p, unit: v }))}>
                <SelectTrigger data-testid="new-fabric-unit" className="h-8 w-24 rounded-none bg-[#141414] border-white/10 text-white text-xs focus:ring-2 focus:ring-white/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                  {units.map((u) => (<SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button onClick={handleAddFabric} data-testid="fabric-add" className="rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors">Add Item</Button>
            </div>

            {/* Units management — separate card. Drives every unit dropdown app-wide. */}
            <div className="mb-4 p-3 border border-white/10 bg-[#0A0A0A]" data-testid="units-manager">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Units</span>
                <span className="text-[10px] text-white/25 normal-case tracking-normal">— manage the options shown in every unit dropdown</span>
                <div className="flex-1" />
                <Input value={newUnitName} onChange={(e) => setNewUnitName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleAddUnit(); }} placeholder="New unit name" data-testid="new-unit-name" className="rounded-none h-8 w-40 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
                <Button onClick={handleAddUnit} data-testid="unit-add" className="rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors">Add Unit</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {units.length === 0 && <span className="text-xs text-white/30">No units yet — add one above.</span>}
                {units.map((u) => (
                  <div key={u.id} data-testid={`unit-chip-${slug(u.name)}`} className="flex items-center gap-2 border border-white/15 bg-[#141414] pl-3 pr-1.5 py-1">
                    <span className="text-sm text-white/90">{u.name}</span>
                    <button onClick={() => handleRenameUnit(u)} data-testid={`unit-rename-${slug(u.name)}`} title="Rename" className="p-1 text-white/40 hover:text-white transition-colors"><Pencil className="h-3 w-3" /></button>
                    <button onClick={() => handleDeleteUnit(u)} data-testid={`unit-delete-${slug(u.name)}`} title="Delete" className="p-1 text-red-400/70 hover:text-red-400 transition-colors"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Add colour under this fabric */}
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 border border-white/10 bg-[#0A0A0A]">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Add colour to {selectedFabric}</span>
              <input type="color" value={newColor.hex} onChange={(e) => setNewColor((p) => ({ ...p, hex: e.target.value }))} data-testid="new-color-hex" className="h-8 w-10 bg-transparent border border-white/10 cursor-pointer" />
              <Input value={newColor.name} onChange={(e) => setNewColor((p) => ({ ...p, name: e.target.value }))} placeholder="Colour name" data-testid="new-color-name" className="rounded-none h-8 w-40 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
              <FormulaInput value={newColor.qty} onValueChange={(val) => setNewColor((p) => ({ ...p, qty: val }))} placeholder="Qty" data-testid="new-color-qty" className="rounded-none h-8 w-24 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
              <Button onClick={handleAddColor} data-testid="add-color-button" className="rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors">Add Colour</Button>
            </div>

            {/* Bulk visibility controls */}
            <div className="flex flex-wrap items-center gap-3 mb-4 p-3 border border-white/10 bg-[#0A0A0A]">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allSelectedInFabric(displayRows)}
                  onCheckedChange={(v) => selectAllFabric(displayRows, !!v)}
                  data-testid="stock-select-all"
                />
                <span className="text-xs uppercase tracking-wider text-white/60">Select all</span>
              </div>
              <div className="h-5 w-px bg-white/15" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Whole fabric</span>
              <Button onClick={() => globalFabricToggle(fabricRows, true)} data-testid="fabric-global-show"
                className="rounded-none h-8 bg-emerald-600 text-white hover:bg-emerald-500 text-xs uppercase transition-colors">
                Show all
              </Button>
              <Button onClick={() => globalFabricToggle(fabricRows, false)} data-testid="fabric-global-hide"
                variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase transition-colors">
                Hide all
              </Button>
              {selectedList.length > 0 && (
                <div className="flex items-center gap-2 ml-auto" data-testid="bulk-action-bar">
                  <span className="text-xs text-white/60 tabular">{selectedList.length} selected</span>
                  <Button onClick={() => runBulk(true)} data-testid="bulk-unhide"
                    className="rounded-none h-8 bg-emerald-600 text-white hover:bg-emerald-500 text-xs uppercase transition-colors">
                    Unhide
                  </Button>
                  <Button onClick={() => runBulk(false)} data-testid="bulk-hide"
                    variant="outline" className="rounded-none h-8 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase transition-colors">
                    Hide
                  </Button>
                  <Button onClick={runBulkDelete} data-testid="bulk-delete"
                    className="rounded-none h-8 bg-red-600 text-white hover:bg-red-500 text-xs uppercase transition-colors">
                    Delete Selected
                  </Button>
                </div>
              )}
            </div>

            <div className="border border-white/10 overflow-x-auto thin-scroll">
              <div className="min-w-[940px]">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 text-xs font-bold uppercase tracking-[0.2em] text-white/40 bg-[#141414]">
                <div className="col-span-1"></div>
                <div className="col-span-3">{isStockSearching ? "Fabric · Colour" : "Colour"}</div>
                <div className="col-span-2 text-right">Current / Alert ≤</div>
                <div className="col-span-2">Set exact ({unitOf(selectedFabric)})</div>
                <div className="col-span-2 text-right">Refill</div>
                <div className="col-span-2 text-center">Show</div>
              </div>
              {displayRows.map((item) => {
                const key = `${item.fabric}-${item.color}`;
                const tid = `${slug(item.fabric)}-${slug(item.color)}`;
                const active = item.active !== false;
                const minKg = item.min_kg;
                const low = minKg != null && minKg > 0 && (item.available_kg ?? 0) <= minKg;
                return (
                  <div key={key} className={`grid grid-cols-12 gap-4 items-center px-5 py-3 border-b border-white/10 transition-colors ${low ? "bg-red-500/10 ring-1 ring-inset ring-red-500/40" : "hover:bg-white/[0.02]"} ${active ? "" : "opacity-45"}`} data-testid={`admin-row-${tid}`}>
                    <div className="col-span-1 flex justify-center">
                      <Checkbox
                        checked={!!selected[selKey(item)]}
                        onCheckedChange={() => toggleSelect(item)}
                        data-testid={`stock-check-${tid}`}
                      />
                    </div>
                    <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                      {(() => {
                        const cd = colorByName[item.color];
                        const cimg = item.image_path || cd?.image_path;
                        return (
                          <button
                            type="button"
                            onClick={() => triggerColorImage(item)}
                            data-testid={`color-image-btn-${tid}`}
                            title={cimg ? "Edit image" : "Upload image"}
                            className="relative h-9 w-9 border border-white/20 bg-[#141414] shrink-0 overflow-hidden group/ci"
                          >
                            {cimg ? (
                              <img src={fileUrl(cimg)} alt={item.color} className="h-full w-full object-cover" />
                            ) : (
                              <span className="h-full w-full flex items-center justify-center"><Images className="h-4 w-4 text-white/20" /></span>
                            )}
                            <span className="absolute inset-0 bg-black/60 opacity-0 group-hover/ci:opacity-100 flex items-center justify-center transition-opacity">
                              <Upload className="h-3.5 w-3.5 text-white" />
                            </span>
                          </button>
                        );
                      })()}
                      {item.image_path && (
                        <button
                          type="button"
                          onClick={() => handleCopyImageToAll(item)}
                          data-testid={`copy-image-all-${tid}`}
                          title="Copy this image to every fabric that has no image for this colour"
                          className="text-white/40 hover:text-white transition-colors shrink-0"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {(() => {
                        const cd = colorByName[item.color];
                        const curHex = (cd || {}).hex || SWATCHES[item.color] || "#888888";
                        if (cd && editSwatchId === cd.id) {
                          return (
                            <span className="flex items-center gap-1 shrink-0" data-testid={`swatch-edit-${tid}`}>
                              <input
                                type="color"
                                value={/^#[0-9a-fA-F]{6}$/.test(swatchVal) ? swatchVal : "#888888"}
                                onChange={(e) => setSwatchVal(e.target.value)}
                                data-testid={`swatch-color-input-${tid}`}
                                className="h-7 w-8 p-0 bg-transparent border border-white/20 cursor-pointer"
                              />
                              <Input
                                value={swatchVal}
                                onChange={(e) => setSwatchVal(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") saveSwatch(cd); if (e.key === "Escape") setEditSwatchId(null); }}
                                data-testid={`swatch-hex-input-${tid}`}
                                className="rounded-none h-7 w-24 bg-[#141414] border-white/20 text-white text-xs focus-visible:ring-2 focus-visible:ring-white/30"
                              />
                              <button onClick={() => saveSwatch(cd)} data-testid={`swatch-save-${tid}`} className="text-emerald-400 hover:text-emerald-300 shrink-0"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setEditSwatchId(null)} data-testid={`swatch-cancel-${tid}`} className="text-white/40 hover:text-white shrink-0"><X className="h-4 w-4" /></button>
                            </span>
                          );
                        }
                        return (
                          <span className="flex items-center gap-1 shrink-0">
                            <span className="h-6 w-6 border border-white/20" style={{ backgroundColor: curHex }} />
                            {cd && (
                              <button
                                onClick={() => startEditSwatch(cd)}
                                data-testid={`swatch-edit-btn-${tid}`}
                                title="Edit swatch colour"
                                className="text-white/40 hover:text-white transition-colors shrink-0"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        );
                      })()}
                      <span className="min-w-0 flex-1">
                        {renameColorId === item.id ? (
                          <span className="flex items-center gap-1">
                            <Input
                              autoFocus
                              value={renameColorVal}
                              onChange={(e) => setRenameColorVal(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveRenameColor(item); if (e.key === "Escape") setRenameColorId(null); }}
                              data-testid={`color-rename-input-${tid}`}
                              className="rounded-none h-7 bg-[#141414] border-white/20 text-white text-xs focus-visible:ring-2 focus-visible:ring-white/30"
                            />
                            <button onClick={() => saveRenameColor(item)} data-testid={`color-rename-save-${tid}`} className="text-emerald-400 hover:text-emerald-300 shrink-0"><Check className="h-4 w-4" /></button>
                            <button onClick={() => setRenameColorId(null)} data-testid={`color-rename-cancel-${tid}`} className="text-white/40 hover:text-white shrink-0"><X className="h-4 w-4" /></button>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 group/rn">
                            <span className="block text-sm text-white truncate">{item.color}</span>
                            <button
                              onClick={() => startRenameColor(item)}
                              data-testid={`color-rename-btn-${tid}`}
                              title="Rename colour (this fabric only)"
                              className="text-white/40 hover:text-white transition-colors shrink-0"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                        {isStockSearching && <span className="block text-[10px] uppercase tracking-wider text-white/40 truncate">{item.fabric}</span>}
                      </span>
                    </div>
                    <div className="col-span-2 flex flex-col items-end gap-1" data-testid={`admin-qty-${tid}`}>
                      <span className={`tabular text-lg ${low ? "text-red-400 font-bold" : "text-white"}`}>{fmt(item.available_kg)}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] uppercase tracking-wider text-white/30">Alert ≤</span>
                        <FormulaInput
                          value={thresholds[key] ?? (item.min_kg ?? "")}
                          onValueChange={(val) => setThresholds((p) => ({ ...p, [key]: val }))}
                          onCommit={() => saveThreshold(item)}
                          data-testid={`admin-threshold-${tid}`}
                          className="h-6 w-14 rounded-none bg-[#141414] border-white/10 text-white text-xs text-right px-1 focus-visible:ring-1 focus-visible:ring-white/30"
                        />
                      </div>
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <FormulaInput
                        value={edits[key] || ""}
                        onValueChange={(val) => setEdits((p) => ({ ...p, [key]: val }))}
                        placeholder={fmt(item.available_kg)}
                        data-testid={`admin-set-input-${tid}`}
                        className="rounded-none h-9 bg-[#141414] border-white/10 text-white placeholder:text-white/20 focus-visible:ring-2 focus-visible:ring-white/30"
                      />
                      <Button
                        onClick={() => handleSet(item)}
                        data-testid={`admin-set-button-${tid}`}
                        className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors"
                      >
                        Set
                      </Button>
                    </div>
                    <div className="col-span-2 flex justify-end gap-2">
                      {[20, 25].map((amt) => (
                        <Button
                          key={amt}
                          variant="outline"
                          onClick={() => handleRefill(item, amt)}
                          data-testid={`admin-refill-${amt}-${tid}`}
                          className="rounded-none h-9 px-2.5 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase transition-colors"
                        >
                          +{amt}
                        </Button>
                      ))}
                    </div>
                    <div className="col-span-2 flex justify-center items-center gap-2">
                      <Switch
                        checked={active}
                        onCheckedChange={(v) => handleToggle(item, v)}
                        data-testid={`admin-toggle-${tid}`}
                      />
                      <button
                        onClick={() => handleDeleteColorRow(item)}
                        data-testid={`admin-delete-${tid}`}
                        title="Delete this colour from the fabric"
                        className="text-white/40 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-black text-2xl tracking-tight text-white">Customer Orders</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={exportCSV}
                  disabled={filteredOrders.length === 0}
                  data-testid="export-orders-button"
                  className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors disabled:opacity-40"
                >
                  <Download className="h-3.5 w-3.5 mr-2" /> Export CSV
                </Button>
                <Button variant="outline" onClick={() => loadAll(token)} className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  <RefreshCw className="h-3.5 w-3.5 mr-2" /> Refresh
                </Button>
              </div>
            </div>

            {/* Filter bar */}
            <div className="border border-white/10 bg-[#0A0A0A] p-4 mb-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-4 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Customer</Label>
                <Input
                  value={filters.q}
                  onChange={(e) => { setFilters((p) => ({ ...p, q: e.target.value })); setOrdersLimit(20); }}
                  placeholder="Name, company or phone"
                  data-testid="orders-filter-search"
                  className="rounded-none h-9 bg-[#141414] border-white/10 text-white placeholder:text-white/25 focus-visible:ring-2 focus-visible:ring-white/30"
                />
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Status</Label>
                <Select value={filters.status} onValueChange={(v) => { setFilters((p) => ({ ...p, status: v })); setOrdersLimit(20); }}>
                  <SelectTrigger data-testid="orders-filter-status" className="h-9 rounded-none bg-[#141414] border-white/10 text-white text-sm focus:ring-2 focus:ring-white/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="Hold">On Hold</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">From</Label>
                <Input
                  type="date"
                  value={filters.from}
                  onChange={(e) => { setFilters((p) => ({ ...p, from: e.target.value })); setOrdersLimit(20); }}
                  data-testid="orders-filter-from"
                  className="rounded-none h-9 bg-[#141414] border-white/10 text-white focus-visible:ring-2 focus-visible:ring-white/30"
                />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">To</Label>
                <Input
                  type="date"
                  value={filters.to}
                  onChange={(e) => { setFilters((p) => ({ ...p, to: e.target.value })); setOrdersLimit(20); }}
                  data-testid="orders-filter-to"
                  className="rounded-none h-9 bg-[#141414] border-white/10 text-white focus-visible:ring-2 focus-visible:ring-white/30"
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  data-testid="orders-filter-clear"
                  className="w-full rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase transition-colors"
                >
                  Clear
                </Button>
              </div>
              <div className="md:col-span-12 text-xs text-white/40 tabular" data-testid="orders-filter-count">
                Showing {orders.length} of {ordersTotal} orders
              </div>
            </div>

            {ordersTotal === 0 && (
              <div className="border border-white/10 py-16 text-center text-white/40 text-sm" data-testid="no-orders">
                No orders match the current filter.
              </div>
            )}

            <div className="space-y-4">
              {filteredOrders.map((o) => {
                const cancelled = o.status === "Cancelled";
                return (
                <div key={o.id} className={`border bg-[#0A0A0A] ${o.urgent && !cancelled ? "border-red-500/60 ring-1 ring-inset ring-red-500/30" : "border-white/10"} ${cancelled ? "opacity-60" : ""}`} data-testid={`order-card-${o.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-white/10 bg-[#141414]">
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium flex items-center flex-wrap gap-x-1">
                        {o.is_coadmin && <span className="text-purple-300">CO-AD — </span>}
                        <span className="truncate" data-testid={`order-client-name-${o.id}`}>{o.client_name}</span>
                        <AlteredCustomerName order={o} testidPrefix={`order-client-altered-${o.id}`} />
                        {!cancelled && <EditPencil onClick={() => setAlterCustomerCtx({ order: o })} testid={`admin-customer-edit-${o.id}`} title="Change customer" />}
                        {o.company_name ? <><span className="text-white/40">·</span> <span className="truncate">{o.company_name}</span></> : null}
                      </p>
                      <p className="text-xs text-white/50 tabular">
                        {o.phone} · {o.created_at ? formatDateTime(o.created_at) : "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {o.is_coadmin && (
                        <span data-testid={`order-coadmin-badge-${o.id}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-purple-500 bg-purple-500/15 text-purple-300 font-bold">
                          Co-Admin
                        </span>
                      )}
                      {o.is_stock_request && (
                        <span data-testid={`order-stockrequest-badge-${o.id}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-sky-500 bg-sky-500/15 text-sky-300 font-bold">
                          Stock Request
                        </span>
                      )}
                      {o.urgent && (
                        <span data-testid={`order-urgent-badge-${o.id}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-red-500 bg-red-500/20 text-red-300 font-bold animate-pulse">
                          ⚑ Urgent
                        </span>
                      )}
                      {o.required_by && (
                        <span data-testid={`order-requiredby-badge-${o.id}`} className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-amber-500/60 bg-amber-500/10 text-amber-300">
                          Required by {formatDate(o.required_by)}
                        </span>
                      )}
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${STATUS_STYLES[o.status] || STATUS_STYLES.Pending}`} data-testid={`order-status-badge-${o.id}`}>
                        {o.status || "Pending"}
                      </span>
                      <span className="tabular text-lg font-display font-black text-white">{fmt(o.total_kg)} {orderUnit(o)}</span>
                    </div>
                  </div>
                  <div className="px-5 py-2 border-b border-white/10 bg-[#0D0D0D]" data-testid={`order-customer-info-${o.id}`}>
                    {o.customer_in_db ? (
                      <p className="text-xs text-white/60">
                        {o.customer_place ? <><span className="text-white/40 uppercase tracking-wider text-[10px]">Place:</span> {o.customer_place}</> : <span className="text-white/30">No place saved</span>}
                        {o.customer_remark ? <>  <span className="text-white/40 uppercase tracking-wider text-[10px] ml-2">Remark:</span> {o.customer_remark}</> : null}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-400/70" data-testid={`order-notindb-${o.id}`}>⚠ Not in customer database</p>
                    )}
                  </div>
                  {o.message && (
                    <div className="px-5 py-3 border-b border-white/10 bg-[#0F0F0F]" data-testid={`order-message-${o.id}`}>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Message from {o.client_name}</p>
                      <p className="text-sm text-white/90 break-words whitespace-pre-wrap">{o.message}</p>
                    </div>
                  )}
                  <div className="divide-y divide-white/10">
                    <div className="flex items-center gap-3 px-5 py-1.5 bg-[#0D0D0D] text-[9px] uppercase tracking-[0.15em] text-white/30">
                      <span className="w-6 shrink-0 text-right">#</span>
                      <span className="w-5 shrink-0" />
                      <span className="flex-1 min-w-0">Item</span>
                      <span className="tabular text-right">Ordered</span>
                      <span className="w-[104px] text-right">Delivered</span>
                      {!cancelled && (
                        <div className="normal-case tracking-normal flex items-center gap-1.5">
                          <button onClick={() => handleAcceptAll(o)} data-testid={`order-accept-all-${o.id}`} title="Accept all pending items"
                            className="inline-flex items-center gap-1 text-[10px] uppercase px-2 py-1 border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-white transition-colors">
                            <Check className="h-3 w-3" /> Accept All
                          </button>
                          <button onClick={() => handleRejectAll(o)} data-testid={`order-reject-all-${o.id}`} title="Reject all pending items"
                            className="inline-flex items-center gap-1 text-[10px] uppercase px-2 py-1 border border-red-500/50 bg-red-500/10 text-red-300 hover:bg-red-500 hover:text-white transition-colors">
                            <X className="h-3 w-3" /> Reject All
                          </button>
                        </div>
                      )}
                    </div>
                    {(o.items || []).map((it, i) => {
                      const istatus = it.status || "Pending";
                      const deleted = !!it.deleted_by;
                      const struck = istatus === "Rejected" || deleted;
                      return (
                      <div key={i} className="flex items-center gap-3 px-5 py-2.5" data-testid={`order-item-${o.id}-${i}`}>
                        <span className="w-6 shrink-0 text-right tabular text-xs text-white/40" data-testid={`order-item-serial-${o.id}-${i}`}>{i + 1}.</span>
                        <span className="h-5 w-5 border border-white/20 shrink-0" style={{ backgroundColor: (colors.find((c) => c.name === it.color) || {}).hex || SWATCHES[it.color] || "#333" }} />
                        <span className="text-sm flex-1 min-w-0 flex flex-col">
                          <AlteredOriginalLine item={it} testid={`order-item-original-${o.id}-${i}`} />
                          <span className={`flex items-center min-w-0 ${struck ? "text-white/40 line-through" : "text-white"}`}>
                            <span className="truncate">{it.fabric} <span className="text-white/40">·</span> {it.color}</span>
                            <AlteredLabel by={it.altered_by} testid={`order-item-altered-label-${o.id}-${i}`} />
                            <DeletedLabel by={it.deleted_by} testid={`order-item-deleted-label-${o.id}-${i}`} />
                          </span>
                        </span>
                        {!cancelled && !deleted && (
                          <>
                            <EditPencil onClick={() => setAlterItemCtx({ order: o, index: i, item: it })} testid={`admin-item-edit-${o.id}-${i}`} title="Alter fabric / colour" />
                            <button onClick={() => handleDeleteItem(o, i)} data-testid={`admin-item-delete-${o.id}-${i}`} title="Delete item (returns qty to stock)" className="shrink-0 p-1 text-red-400/60 hover:text-red-400 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                        {!cancelled && deleted && (
                          <button onClick={() => handleRestoreItem(o, i)} data-testid={`admin-item-restore-${o.id}-${i}`} title="Undo delete — restore item & re-deduct stock" className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400/80 hover:text-amber-300 border border-amber-500/30 hover:border-amber-400 px-1.5 py-0.5 transition-colors"><Undo2 className="h-3 w-3" /> Undo</button>
                        )}
                        <span className="tabular text-sm text-white">{fmt(it.quantity_kg)} {unitOf(it.fabric)}</span>
                        <DeliveredCell
                          item={it}
                          unit={unitOf(it.fabric)}
                          disabled={cancelled || istatus === "Rejected" || deleted}
                          onSave={(v) => handleDelivered(o, i, v)}
                          testid={`order-item-delivered-${o.id}-${i}`}
                        />
                        <span className={`text-[10px] uppercase tracking-wider ${it.available ? "text-emerald-400" : "text-red-400"}`} data-testid={`order-item-avail-${o.id}-${i}`}>
                          {it.available ? "Available" : "Not Avail."}
                        </span>
                        <span className={`text-[10px] uppercase tracking-wider w-16 text-right ${ITEM_STYLES[istatus]}`} data-testid={`order-item-status-${o.id}-${i}`}>
                          {istatus}
                        </span>
                        {!cancelled && !deleted && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleItemDecision(o, i, istatus === "Accepted" ? "Pending" : "Accepted")}
                              data-testid={`item-accept-${o.id}-${i}`}
                              title="Accept item"
                              className={`h-7 w-7 flex items-center justify-center border transition-colors ${
                                istatus === "Accepted" ? "bg-emerald-500 border-emerald-500 text-white" : "border-white/20 text-white/60 hover:border-emerald-500 hover:text-emerald-400"
                              }`}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleItemDecision(o, i, istatus === "Rejected" ? "Pending" : "Rejected")}
                              data-testid={`item-reject-${o.id}-${i}`}
                              title="Reject item (returns stock)"
                              className={`h-7 w-7 flex items-center justify-center border transition-colors ${
                                istatus === "Rejected" ? "bg-red-500 border-red-500 text-white" : "border-white/20 text-white/60 hover:border-red-500 hover:text-red-400"
                              }`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-white/10">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 mr-1">Action</span>
                    <Button
                      onClick={() => handleStatus(o, "Approved")}
                      disabled={cancelled || o.status === "Approved"}
                      data-testid={`order-approve-${o.id}`}
                      className="rounded-none h-9 bg-emerald-600 text-white hover:bg-emerald-500 text-xs uppercase tracking-wider transition-colors disabled:opacity-30"
                    >
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button
                      onClick={() => handleStatus(o, "Rejected")}
                      disabled={cancelled || o.status === "Rejected"}
                      data-testid={`order-reject-${o.id}`}
                      variant="outline"
                      className="rounded-none h-9 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase tracking-wider transition-colors disabled:opacity-30"
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                    <Button
                      onClick={() => handleStatus(o, "Hold")}
                      disabled={cancelled || o.status === "Hold"}
                      data-testid={`order-hold-${o.id}`}
                      variant="outline"
                      className="rounded-none h-9 border-orange-500/40 bg-transparent text-orange-400 hover:bg-orange-500 hover:text-white text-xs uppercase tracking-wider transition-colors disabled:opacity-30"
                    >
                      <Clock className="h-3.5 w-3.5 mr-1" /> Hold
                    </Button>
                    <Button
                      onClick={() => handleCancel(o)}
                      disabled={cancelled}
                      data-testid={`order-cancel-${o.id}`}
                      variant="outline"
                      className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors disabled:opacity-30"
                    >
                      <Ban className="h-3.5 w-3.5 mr-1" /> {cancelled ? "Cancelled" : "Cancel"}
                    </Button>
                  </div>
                  <div className="px-5 py-3 border-t border-white/10 bg-[#0F0F0F]">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1.5">Reply to customer (shown in their order history)</Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <textarea
                        value={replies[o.id] ?? o.admin_reply ?? ""}
                        maxLength={499}
                        rows={2}
                        onChange={(e) => setReplies((p) => ({ ...p, [o.id]: e.target.value.slice(0, 499) }))}
                        data-testid={`order-reply-input-${o.id}`}
                        placeholder="Type a message the customer will see…"
                        className="flex-1 rounded-none bg-[#141414] border border-white/10 text-white text-sm p-2.5 placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 resize-none"
                      />
                      <Button
                        onClick={() => saveReply(o)}
                        data-testid={`order-reply-save-${o.id}`}
                        className="rounded-none h-9 sm:h-auto sm:self-stretch bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider transition-colors px-4"
                      >
                        Send
                      </Button>
                    </div>
                    {o.admin_reply && (
                      <p className="mt-2 text-[11px] text-emerald-400" data-testid={`order-reply-current-${o.id}`}>Current reply: “{o.admin_reply}”</p>
                    )}
                  </div>
                  <OrderPricing token={token} order={o} onSaved={() => loadOrders(token)} />
                </div>
                );
              })}
            </div>

            {orders.length < ordersTotal && (
              <div className="flex justify-center pt-5" data-testid="orders-load-more-wrap">
                <Button
                  variant="outline"
                  onClick={() => setOrdersLimit((l) => l + 20)}
                  data-testid="orders-load-more"
                  className="rounded-none h-10 px-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors"
                >
                  Load More ({ordersTotal - orders.length} more)
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Colours */}
          <TabsContent value="colors" className="mt-6">
            <h2 className="font-display font-black text-2xl tracking-tight text-white mb-4">Edit Colours</h2>
            <div className="border border-white/10">
              {colors.map((c) => {
                const e = colorEdits[c.id] || {};
                const name = e.name !== undefined ? e.name : c.name;
                const hex = e.hex !== undefined ? e.hex : c.hex;
                const dirty = name !== c.name || hex !== c.hex;
                return (
                  <div key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-white/10" data-testid={`color-row-${c.id}`}>
                    <span className="h-8 w-8 border border-white/20 shrink-0" style={{ backgroundColor: hex }} />
                    <input
                      type="color"
                      value={hex}
                      onChange={(ev) => setColorEdits((p) => ({ ...p, [c.id]: { ...e, hex: ev.target.value } }))}
                      data-testid={`color-hex-${c.id}`}
                      className="h-9 w-12 bg-transparent border border-white/10 cursor-pointer"
                    />
                    <Input
                      value={name}
                      onChange={(ev) => setColorEdits((p) => ({ ...p, [c.id]: { ...e, name: ev.target.value } }))}
                      data-testid={`color-name-${c.id}`}
                      className="rounded-none h-9 flex-1 min-w-[160px] bg-[#141414] border-white/10 text-white focus-visible:ring-2 focus-visible:ring-white/30"
                    />
                    <Button
                      onClick={() => handleColorSave(c)}
                      disabled={!dirty}
                      data-testid={`color-save-${c.id}`}
                      className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors disabled:opacity-30"
                    >
                      Save
                    </Button>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Rates */}
          <TabsContent value="rates" className="mt-6">
            <RatesManager token={token} fabrics={fabrics} stock={stock} />
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports" className="mt-6">
            <ReportsTab token={token} />
          </TabsContent>

          {/* Catalogs */}
          <TabsContent value="catalog" className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-display font-black text-2xl tracking-tight text-white">Catalog Images</h2>
              <div className="flex items-center gap-2">
                <Select value={catalogFabric || "none"} onValueChange={(v) => setCatalogFabric(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="catalog-upload-fabric" className="rounded-none h-9 w-52 bg-[#141414] border-white/10 text-white text-xs focus:ring-2 focus:ring-white/30">
                    <SelectValue placeholder="Tag fabric (optional)" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                    <SelectItem value="none">No fabric tag</SelectItem>
                    {fabrics.map((f) => (
                      <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label data-testid="catalog-upload-label" className="cursor-pointer inline-flex items-center gap-2 rounded-none bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider font-semibold px-4 py-2 transition-colors">
                  <Upload className="h-3.5 w-3.5" /> Upload Image
                  <input type="file" accept="image/*" className="hidden" data-testid="catalog-upload-input"
                    onChange={(ev) => { handleCatalogUpload(ev.target.files[0]); ev.target.value = ""; }} />
                </label>
              </div>
            </div>
            {catalog.length === 0 && (
              <div className="border border-white/10 py-16 text-center text-white/40 text-sm" data-testid="catalog-admin-empty">No images yet. Upload the first one.</div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {catalog.map((it) => {
                const e = catalogEdits[it.id] || {};
                const title = e.title !== undefined ? e.title : (it.title || "");
                const fabric = e.fabric !== undefined ? e.fabric : (it.fabric || "");
                const dirty = title !== (it.title || "") || fabric !== (it.fabric || "");
                return (
                  <div key={it.id} className="border border-white/10 bg-[#141414] group relative flex flex-col" data-testid={`catalog-admin-${it.id}`}>
                    <div className="aspect-[4/5] overflow-hidden">
                      <img src={fileUrl(it.storage_path)} alt={it.title} loading="lazy" className="h-full w-full object-cover" />
                    </div>
                    <button
                      onClick={() => handleCatalogDelete(it.id)}
                      data-testid={`catalog-delete-${it.id}`}
                      className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center bg-black/70 border border-white/20 text-white hover:bg-red-500 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="p-2 space-y-2 border-t border-white/10">
                      <Input
                        value={title}
                        onChange={(ev) => setCatalogEdits((p) => ({ ...p, [it.id]: { ...e, title: ev.target.value } }))}
                        placeholder="Image name"
                        data-testid={`catalog-title-${it.id}`}
                        className="rounded-none h-8 bg-[#0A0A0A] border-white/10 text-white text-xs focus-visible:ring-2 focus-visible:ring-white/30"
                      />
                      <Select value={fabric || "none"} onValueChange={(v) => setCatalogEdits((p) => ({ ...p, [it.id]: { ...e, fabric: v === "none" ? "" : v } }))}>
                        <SelectTrigger data-testid={`catalog-fabric-${it.id}`} className="rounded-none h-8 bg-[#0A0A0A] border-white/10 text-white text-xs focus:ring-2 focus:ring-white/30">
                          <SelectValue placeholder="Fabric" />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white">
                          <SelectItem value="none">No fabric tag</SelectItem>
                          {fabrics.map((f) => (
                            <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={() => handleCatalogSave(it)}
                        disabled={!dirty}
                        data-testid={`catalog-save-${it.id}`}
                        className="w-full rounded-none h-8 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors disabled:opacity-30"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Activity */}
          <TabsContent value="activity" className="mt-6">
            <h2 className="font-display font-black text-2xl tracking-tight text-white mb-4">Recent Customer Activity</h2>
            <div className="border border-white/10">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 text-xs font-bold uppercase tracking-[0.2em] text-white/40 bg-[#141414]">
                <div className="col-span-4">Customer</div>
                <div className="col-span-3">Company</div>
                <div className="col-span-3">Viewed</div>
                <div className="col-span-2 text-right">When</div>
              </div>
              {views.length === 0 && (
                <div className="py-12 text-center text-white/40 text-sm" data-testid="no-activity">No activity logged yet.</div>
              )}
              {views.slice(0, activityLimit).map((v) => (
                <div key={v.id} className="grid grid-cols-12 gap-4 px-5 py-2.5 border-b border-white/10 text-sm" data-testid={`view-row-${v.id}`}>
                  <div className="col-span-4 text-white truncate">{v.client_name} <span className="text-white/30 tabular">· {v.phone}</span></div>
                  <div className="col-span-3 text-white/70 truncate">{v.company_name}</div>
                  <div className="col-span-3 text-white/80 truncate" data-testid={`view-detail-${v.id}`}>
                    {v.type === "catalog" ? (
                      "Catalogs"
                    ) : (
                      <>
                        <span>{v.ref}</span>
                        {v.color && <span className="block text-[11px] text-white/45 truncate">· {v.color}</span>}
                      </>
                    )}
                  </div>
                  <div className="col-span-2 text-right text-xs text-white/40">{v.created_at ? formatDateTime(v.created_at) : "—"}</div>
                </div>
              ))}
            </div>
            {views.length > activityLimit && (
              <div className="flex justify-center pt-5">
                <Button variant="outline" onClick={() => setActivityLimit((l) => l + 20)} data-testid="activity-load-more" className="rounded-none h-10 px-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  Load More ({views.length - activityLimit} more)
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Low Stock dashboard (live, derived from current stock) */}
          <TabsContent value="low-stock" className="mt-6">
            <LowStockTab token={token} stock={stock} fabrics={fabrics} colors={colors} onStockRefresh={() => loadAll(token)} />
          </TabsContent>

          {/* Stock History (read-only, isolated log) */}
          <TabsContent value="stock-history" className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-display font-black text-2xl tracking-tight text-white">Stock History</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportStockHistory} data-testid="stock-history-export" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  <Download className="h-3.5 w-3.5 mr-2" /> Export CSV
                </Button>
                <Button variant="outline" onClick={() => loadStockHistory()} data-testid="stock-history-refresh" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                  <RefreshCw className="h-3.5 w-3.5 mr-2" /> Refresh
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="border border-white/10 bg-[#0A0A0A] p-4 mb-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-3 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Fabric</Label>
                <Select value={historyFilters.fabric} onValueChange={(v) => setHistoryFilters((p) => ({ ...p, fabric: v }))}>
                  <SelectTrigger data-testid="history-filter-fabric" className="h-9 rounded-none bg-[#141414] border-white/10 text-white text-sm focus:ring-2 focus:ring-white/30">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white max-h-72">
                    <SelectItem value="all">All fabrics</SelectItem>
                    {fabrics.map((f) => (<SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">Colour</Label>
                <Input
                  value={historyFilters.color}
                  onChange={(e) => setHistoryFilters((p) => ({ ...p, color: e.target.value }))}
                  placeholder="Colour name"
                  data-testid="history-filter-color"
                  className="rounded-none h-9 bg-[#141414] border-white/10 text-white placeholder:text-white/25 focus-visible:ring-2 focus-visible:ring-white/30"
                />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">From</Label>
                <Input type="date" value={historyFilters.date_from} onChange={(e) => setHistoryFilters((p) => ({ ...p, date_from: e.target.value }))} data-testid="history-filter-from" className="rounded-none h-9 bg-[#141414] border-white/10 text-white focus-visible:ring-2 focus-visible:ring-white/30" />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40">To</Label>
                <Input type="date" value={historyFilters.date_to} onChange={(e) => setHistoryFilters((p) => ({ ...p, date_to: e.target.value }))} data-testid="history-filter-to" className="rounded-none h-9 bg-[#141414] border-white/10 text-white focus-visible:ring-2 focus-visible:ring-white/30" />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button onClick={() => loadStockHistory()} data-testid="history-apply-filter" className="flex-1 rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors">Apply</Button>
                <Button variant="outline" onClick={() => { const r = { fabric: "all", color: "", date_from: "", date_to: "" }; setHistoryFilters(r); loadStockHistory(r); }} data-testid="history-clear-filter" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase transition-colors">Clear</Button>
              </div>
            </div>

            <div className="border border-white/10 overflow-x-auto thin-scroll">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 text-xs font-bold uppercase tracking-[0.2em] text-white/40 bg-[#141414]">
                  <div className="col-span-3">Fabric</div>
                  <div className="col-span-2">Colour</div>
                  <div className="col-span-1 text-right">Prev</div>
                  <div className="col-span-1 text-right">New</div>
                  <div className="col-span-2">Change</div>
                  <div className="col-span-3 text-right">When</div>
                </div>
                {stockHistory.length === 0 && (
                  <div className="py-12 text-center text-white/40 text-sm" data-testid="no-stock-history">No stock changes logged yet.</div>
                )}
                {stockHistory.slice(0, historyLimit).map((h) => {
                  const delta = (h.new_qty ?? 0) - (h.prev_qty ?? 0);
                  const badge = h.change_type === "Order Deducted" ? "text-red-400 border-red-500/30"
                    : h.change_type === "Order Cancelled" ? "text-emerald-400 border-emerald-500/30"
                    : "text-white/70 border-white/20";
                  return (
                    <div key={h.id} className="grid grid-cols-12 gap-4 px-5 py-2.5 border-b border-white/10 text-sm items-center" data-testid={`stock-history-row-${h.id}`}>
                      <div className="col-span-3 text-white truncate">{h.fabric}</div>
                      <div className="col-span-2 text-white/80 truncate">{h.color}</div>
                      <div className="col-span-1 text-right tabular text-white/60">{fmt(h.prev_qty)}</div>
                      <div className="col-span-1 text-right tabular text-white">{fmt(h.new_qty)}</div>
                      <div className="col-span-2">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border ${badge}`}>{h.change_type}</span>
                        <span className={`ml-1 text-[10px] tabular ${delta < 0 ? "text-red-400" : "text-emerald-400"}`}>{delta > 0 ? "+" : ""}{fmt(delta)}</span>
                        {h.order_id && (
                          <span className="block mt-0.5 text-[10px] text-white/45 truncate" data-testid={`history-order-${h.id}`} title={`Order ${h.order_id}`}>
                            Order: {h.order_ref || "—"}{h.order_company ? ` · ${h.order_company}` : ""} <span className="text-white/25">#{String(h.order_id).slice(-6)}</span>
                          </span>
                        )}
                      </div>
                      <div className="col-span-3 text-right text-xs text-white/40">{h.created_at ? formatDateTime(h.created_at) : "—"}</div>
                    </div>
                  );
                })}
                {stockHistory.length > historyLimit && (
                  <div className="flex justify-center py-4">
                    <Button variant="outline" onClick={() => setHistoryLimit((l) => l + 20)} data-testid="stock-history-load-more" className="rounded-none h-10 px-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider transition-colors">
                      Load More ({stockHistory.length - historyLimit} more)
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Suppliers (admin-only, isolated collections) */}
          <TabsContent value="suppliers" className="mt-6">
            <SuppliersTab token={token} fabrics={fabrics} stock={stock} colors={colors} units={units} onStockRefresh={() => loadAll(token)} />
          </TabsContent>

          <TabsContent value="instructions" className="mt-6">
            <InstructionsManager token={token} />
          </TabsContent>

          <TabsContent value="customers" className="mt-6">
            <CustomerDatabase token={token} />
          </TabsContent>

          <TabsContent value="coadmin" className="mt-6">
            <CoAdminManager token={token} />
            <CoAdminActivity token={token} />
          </TabsContent>

          {/* Announcement + Data Audit */}
          <TabsContent value="announcement" className="mt-6">
            <div className="max-w-2xl space-y-6">
              <div className="border border-white/10 bg-[#0A0A0A] p-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="font-display font-black text-2xl tracking-tight text-white">Announcement Banner</h2>
                  <button
                    type="button"
                    onClick={() => setAnnEnabled((v) => !v)}
                    data-testid="announcement-toggle"
                    className={`relative h-6 w-11 rounded-full transition-colors ${annEnabled ? "bg-emerald-500" : "bg-white/20"}`}
                    aria-pressed={annEnabled}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-all ${annEnabled ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
                <p className="text-xs text-white/50 mb-2">{annEnabled ? "Banner is ON — customers see it live." : "Banner is OFF — no banner shows for customers."}</p>
                <Textarea
                  value={annMsg}
                  maxLength={499}
                  onChange={(e) => setAnnMsg(e.target.value.slice(0, 499))}
                  placeholder="Type your announcement for customers…"
                  data-testid="announcement-input"
                  className="rounded-none bg-[#141414] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-white/30 min-h-28"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-xs tabular ${annMsg.length >= 499 ? "text-red-400" : "text-white/40"}`} data-testid="announcement-counter">{annMsg.length}/499</span>
                  <Button onClick={handleSaveAnnouncement} disabled={annSaving} data-testid="announcement-save" className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider transition-colors">
                    {annSaving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Audit & Cleanup Tool */}
        </Tabs>
      </div>

      {/* Bulk colour image preview / confirm */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { bulkRows.forEach((r) => r.preview && URL.revokeObjectURL(r.preview)); setBulkRows([]); } setBulkOpen(o); }}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white sm:max-w-2xl rounded-none flex flex-col max-h-[88vh]" data-testid="bulk-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">Confirm colour images · {bulkFabric}</DialogTitle>
            <DialogDescription className="text-white/50">
              Review each match, fix any mismatch or assign an unmatched image, then confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 flex-1 overflow-y-auto thin-scroll space-y-px bg-white/10 border border-white/10">
            {bulkRows.map((r) => {
              const cols = colorsForFabric(bulkFabric);
              const matched = !!r.colorName;
              return (
                <div key={r.key} className="bg-[#0A0A0A] p-3 flex items-center gap-3" data-testid={`bulk-row-${r.key}`}>
                  <span className="h-12 w-12 border border-white/20 shrink-0 overflow-hidden bg-[#141414]">
                    <img src={r.preview} alt={r.filename} className="h-full w-full object-cover" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-white/70 truncate">{r.filename}</p>
                    {!matched && (
                      <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wider text-amber-400 border border-amber-500/40 px-1.5 py-0.5" data-testid={`bulk-unmatched-${r.key}`}>Unmatched</span>
                    )}
                  </div>
                  <Select value={r.colorName || "none"} onValueChange={(v) => setBulkRowColor(r.key, v === "none" ? "" : v)}>
                    <SelectTrigger data-testid={`bulk-color-select-${r.key}`} className={`w-48 h-9 rounded-none bg-[#141414] text-white text-sm focus:ring-2 focus:ring-white/30 ${matched ? "border-white/10" : "border-amber-500/50"}`}>
                      <SelectValue placeholder="Assign colour" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[#141414] border-white/10 text-white max-h-72">
                      <SelectItem value="none">— Skip —</SelectItem>
                      {cols.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-xs text-white/40 tabular">
              {bulkRows.filter((r) => r.colorName).length} matched · {bulkRows.filter((r) => !r.colorName).length} unmatched
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel-btn" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase transition-colors">Cancel</Button>
              <Button onClick={confirmBulkUpload} disabled={bulkSaving} data-testid="bulk-confirm-btn" className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase transition-colors">
                {bulkSaving ? "Saving…" : "Confirm & Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlterItemDialog
        open={!!alterItemCtx}
        onOpenChange={(o) => { if (!o) setAlterItemCtx(null); }}
        stockOptions={stock}
        item={alterItemCtx?.item}
        onSave={handleAlterItem}
      />
      <CustomerPickerDialog
        open={!!alterCustomerCtx}
        onOpenChange={(o) => { if (!o) setAlterCustomerCtx(null); }}
        searchCustomers={async (q) => { const r = await getCustomers(token, { q, skip: 0, limit: 30 }); return r.items || []; }}
        onPick={handleAlterCustomer}
      />
    </div>
  );
}
